// =============================================================
// The Play — scroll-SCRUBBED animation
//
// The section is a 300vh scroll-track with a sticky stage pinned to
// the viewport. Animation progress is tied 1:1 to scroll position:
// every scroll offset between the top and bottom of the track maps to
// a fixed point in the animation, so scrolling up/down scrubs it back
// and forth rather than triggering fixed-duration scenes.
//
// Two phases split the 0–1 progress at the midpoint:
//   0%–50%  : phase A — heading fades out; hero slides up (85vh → 0)
//             and pops in (opacity ramps over the first part of the slide)
//   50%–100%: phase B — hero scales down (1 → 0.85); tagline arrives:
//             slides up (25vh → 0), fades in, scales 2.5 → 1
// =============================================================

export {}; // mark as a module so top-level consts stay file-scoped

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

const scrollTrack = document.querySelector<HTMLElement>(".section-play__scroll-track");
const heading = document.querySelector<HTMLElement>(".section-play__heading");
const hero = document.querySelector<HTMLElement>(".section-play__hero");
const tagline = document.querySelector<HTMLElement>(".section-play__tagline");

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

if (!scrollTrack || !heading || !hero || !tagline) {
  console.warn("[play-scroll] missing element(s); animation disabled");
} else {
  // Apply the animation at a given progress (0–1) by writing inline styles
  // directly — no timed animation, so the frame always reflects scroll.
  const render = (progress: number) => {
    // Split progress into the two phases (each 0–1 within its half).
    const phaseA = clamp01(progress / 0.5);
    const phaseB = clamp01((progress - 0.5) / 0.5);

    // Phase A — heading out, hero in and up.
    const headingOpacity = 1 - phaseA;
    const heroY = lerp(85, 0, phaseA);
    // Hero opacity reaches full over the first ~third of its slide, so it
    // pops in (transparent → opaque) as the move begins.
    const heroOpacity = clamp01(phaseA / 0.3);

    // Phase B — hero scales down, tagline arrives.
    const heroScale = lerp(1, 0.85, phaseB);
    const taglineOpacity = phaseB;
    const taglineY = lerp(25, 0, phaseB);
    const taglineScale = lerp(2.5, 1, phaseB);

    heading.style.opacity = String(headingOpacity);
    hero.style.opacity = String(heroOpacity);
    hero.style.transform = `translateY(${heroY}vh) scale(${heroScale})`;
    tagline.style.opacity = String(taglineOpacity);
    tagline.style.transform = `translateY(${taglineY}vh) scale(${taglineScale})`;
  };

  if (prefersReducedMotion) {
    // No scrubbing — snap to the final state.
    render(1);
  } else {
    // Throttle scroll handling to one render per animation frame.
    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = scrollTrack.getBoundingClientRect();
      const totalDistance = rect.height - window.innerHeight;
      if (totalDistance <= 0) {
        return;
      }
      // rect.top goes negative once the track's top passes the viewport top;
      // -rect.top is how far we've scrolled into the track.
      const progress = clamp01(-rect.top / totalDistance);
      render(progress);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
  }
}
